import api from './api';

// ── Instagram comment management (Comments page) ────────────────────────────

export interface InstagramMedia {
  id: string;
  caption?: string;
  media_type?: string; // IMAGE | VIDEO | CAROUSEL_ALBUM
  media_url?: string;
  thumbnail_url?: string;
  permalink?: string;
  timestamp?: string;
  comments_count?: number;
  like_count?: number;
}

export interface InstagramComment {
  id: string;
  text: string;
  timestamp?: string;
  username?: string;
  like_count: number;
  hidden: boolean;
  is_own: boolean; // written by the connected account itself
  replies?: InstagramComment[];
}

// updated_at / refreshing: the list is stored and served at once; refreshing
// means a newer one is being read from Instagram in the background.
export type StoredFreshness = { updated_at?: string | null; refreshing?: boolean };

export type InstagramMediaResponse =
  | ({
      status: 'ok';
      account: { id: string; username?: string; profile_picture_url?: string };
      media: InstagramMedia[];
    } & StoredFreshness)
  | { status: 'not_connected' | 'reconnect_required'; message: string };

const IG_COMMENTS = '/smm/comments/instagram';

class SmmApiService {
  /**
   * Get Social Presence Overview
   * GET /smm/strategy/social-presence/{user_id}
   */
  async getSocialPresence(userId: string) {
    return api.get<any>(`/smm/strategy/social-presence/${userId}`);
  }

  /**
   * Get Communication Direction
   * GET /smm/strategy/communication-direction/{user_id}
   */
  async getCommunicationDirection(userId: string) {
    return api.get<any>(`/smm/strategy/communication-direction/${userId}`);
  }

  /**
   * Get Audience Intelligence
   * GET /smm/strategy/audience-intelligence/{user_id}
   */
  async getAudienceIntelligence(userId: string) {
    return api.get<any>(`/smm/strategy/audience-intelligence/${userId}`);
  }

  async getSmmGovernanceSummary(userId: string, month: string) {
    return api.get<any>(`/smm/governance/summary/${userId}/${month}`);
  }

  async getSmmGovernancePosts(userId: string, month: string) {
    return api.get<any[]>(`/smm/governance/posts/${userId}/${month}`);
  }

  async getSmmPerformance(userId: string, month: string) {
    return api.get<any>(`/smm/governance/performance/${userId}/${month}`);
  }

  /**
   * Update Post Governance Status
   * PATCH /content-calendar/posts/{post_id}/governance-status
   */
  async updateGovernanceStatus(postId: string, governanceStatus: string) {
    return api.patch<any>(`/content-calendar/posts/${encodeURIComponent(postId)}/governance-status`, {
      governanceStatus,
    });
  }

  /**
   * Update Post Status
   * PATCH /content-calendar/posts/{post_id}/status
   */
  async updatePostStatus(postId: string, status: string) {
    return api.patch<any>(`/content-calendar/posts/${encodeURIComponent(postId)}/status`, {
      status,
    });
  }

  // Instagram comments — all act on the signed-in user's connected account.

  // refresh: the page's Refresh button (stored list now, newer one in the background)
  async getInstagramMedia(limit = 12, refresh = false) {
    return api.get<InstagramMediaResponse>(`${IG_COMMENTS}/media?limit=${limit}${refresh ? '&refresh=true' : ''}`);
  }

  async getInstagramComments(mediaId: string, refresh = false) {
    return api.get<{ media: InstagramMedia; comments: InstagramComment[] } & StoredFreshness>(
      `${IG_COMMENTS}/media/${encodeURIComponent(mediaId)}/comments${refresh ? '?refresh=true' : ''}`,
    );
  }

  async addInstagramComment(mediaId: string, message: string) {
    return api.post<{ id: string }>(`${IG_COMMENTS}/media/${encodeURIComponent(mediaId)}/comments`, { message });
  }

  async replyToInstagramComment(commentId: string, message: string) {
    return api.post<{ id: string }>(`${IG_COMMENTS}/comments/${encodeURIComponent(commentId)}/replies`, { message });
  }

  async setInstagramCommentHidden(commentId: string, hidden: boolean) {
    return api.post<{ id: string; hidden: boolean }>(
      `${IG_COMMENTS}/comments/${encodeURIComponent(commentId)}/hide`,
      { hidden },
    );
  }

  async deleteInstagramComment(commentId: string) {
    return api.delete<{ id: string; deleted: boolean }>(`${IG_COMMENTS}/comments/${encodeURIComponent(commentId)}`);
  }
}

export const smmApi = new SmmApiService();
export default smmApi;
